/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import { htmlToText } from 'html-to-text';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { CmsImage } from '../CmsImage';
import { FeLink } from '../FeLink';

export const ArticleComponent: React.FC<ArticleProps> = ({
  url,
  title,
  subtitle,
  imageUrl,
  category,
  date,
  pushDataLayer,
}) => {
  const index: number = 0;

  const targetAttr = index ? '_self' : '_blank';
  const relAttr = index ? '' : 'noopener noreferrer';

  const handleArticleClick = () => {
    if (pushDataLayer) {
      pushDataLayer({
        event: 'navigate',
        component_type: 'article-list',
        component_title: `${title}`,
        action: 'click',
        index: index,
      });
    }
  };

  const parseHtmlToString = (html?: string | null) => {
    if (!html) return '';
    return htmlToText(html, {
      wordwrap: false,
    });
  };

  return (
    <div className="flex h-full flex-col gap-spacing/gap/soft-12px overflow-hidden p-spacing/padding/soft-12px lg:flex-row">
      {imageUrl && (
        <div className="w-full">
          <div className="h-24 w-full rounded-radius/card-inner md:h-[222px] lg:h-32 lg:w-48">
            <CmsImage
              // The slot is 192x128 CSS px at lg (`lg:h-32 lg:w-48`) and full-width below it, so the
              // browser can take a `small_`/`thumbnail_` bucket instead of the editor's upload —
              // 118 kB down to 24 kB on a measured cover. `unoptimized` used to sit here and turned
              // off both the loader and srcSet; it is the flag every other CmsImage consumer was
              // moved off, and this card now appears on four surfaces (tag archive, related
              // articles, vertical landing, author) rather than two.
              sizes={{ _: '100vw', lg: 192 }}
              src={imageUrl}
              height={222}
              width={334}
              alt={title}
              className="size-full rounded-radius/card-inner object-cover"
            />
          </div>
        </div>
      )}
      <div className="flex min-w-0 shrink flex-col gap-spacing/gap/soft-6px px-spacing/gap/soft-6px py-spacing/gap/soft-4px">
        <h3 className="mhg-alpha-typography-title-2 line-clamp-2 cursor-pointer text-text/primary">
          <FeLink
            href={url}
            target={targetAttr}
            rel={relAttr}
            onClick={handleArticleClick}
          >
            {title}
          </FeLink>
        </h3>
        <FeLink
          href={url}
          target={targetAttr}
          rel={relAttr}
          onClick={handleArticleClick}
        >
          {subtitle && (
            <p className="mhg-alpha-body-1-relaxed line-clamp-2 text-text/secondary-lighter">
              {parseHtmlToString(subtitle)}
            </p>
          )}
        </FeLink>
        {(category || date) && (
          <div className="mhg-alpha-typography-caption flex flex-wrap items-center gap-spacing/gap/soft-6px text-text/secondary-lighter">
            {category && <span>{category}</span>}
            {category && date && <span aria-hidden="true">·</span>}
            {date && <span>{date}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export type ArticleProps = {
  url: string;
  title: string;
  subtitle: string | null;
  imageUrl?: string | null;
  /** Editorial category label (article page_category) shown under the excerpt. */
  category?: string | null;
  /** Pre-formatted date string shown under the excerpt. */
  date?: string | null;
  pushDataLayer: PushDataLayerFn | undefined;
};
